import { useEffect, useState } from "react"

import { Loader2, RefreshCw } from "lucide-react"
import { useTranslation } from "react-i18next"

import { cn } from "@/shared/lib/utils"

const SLOW_LOADING_MS = 10_000

interface NeuralNetworkLoaderProps {
  className?: string
  context?: string
  /** Restarts the load; without it "Try again" reloads the page. */
  onRetry?: () => void
}

export const NeuralNetworkLoader = ({ className, context, onRetry }: NeuralNetworkLoaderProps) => {
  const { t, ready } = useTranslation()
  const [attempt, setAttempt] = useState(0)
  const [slowAttempt, setSlowAttempt] = useState<number | null>(null)
  const isSlow = slowAttempt === attempt

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setSlowAttempt(attempt)
    }, SLOW_LOADING_MS)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [attempt])

  const handleRetry = () => {
    if (!onRetry) {
      window.location.reload()
      return
    }
    setAttempt(current => current + 1)
    onRetry()
  }

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn("flex w-full flex-col items-center justify-center gap-3 px-4 py-10 text-center", className)}>
      <Loader2
        aria-hidden="true"
        focusable="false"
        className="size-6 animate-spin text-yellow-500 motion-reduce:animate-none"
      />
      <p
        aria-hidden={ready ? undefined : true}
        className={cn("text-sm text-gray-400", !ready && "invisible")}>
        {ready ? (context ?? t("common-loading", "Loading...")) : " "}
      </p>
      {isSlow ? (
        <>
          <p className="max-w-xs text-sm text-gray-300">
            {t("loading-slow", "This is taking longer than usual. Check your connection and try again.")}
          </p>
          <button
            type="button"
            onClick={handleRetry}
            className="inline-flex min-h-10 items-center gap-2 rounded-xl bg-gray-700 px-4 text-sm font-medium text-gray-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-gray-400 hover-device:hover:bg-gray-600">
            <RefreshCw
              aria-hidden="true"
              focusable="false"
              className="size-4"
            />
            {t("common:error-boundary-try-again", "Try again")}
          </button>
        </>
      ) : null}
    </div>
  )
}
